import { useTranslation } from 'react-i18next';
import { Card, CardContent } from '../../ui/card';
import type { UserInsight } from '../../../types/insight';

interface InsightsSummaryRowProps {
  insight: UserInsight;
}

/**
 * Stat tiles, not a chart: these are a handful of headline numbers, and a
 * one-bar chart per number would say less.
 *
 * Tiles with nothing to show are dropped rather than rendered as "—". A tile
 * that is permanently empty teaches the reader to ignore the row.
 */
export default function InsightsSummaryRow({ insight }: InsightsSummaryRowProps) {
  const { t } = useTranslation();

  const tiles: Array<{ key: string; value: string; label: string }> = [
    {
      key: 'sends',
      value: String(insight.qualifying_sends_count),
      label: t('userProfile.insights.tiles.sends'),
    },
  ];

  if (insight.attempts_efficiency !== null) {
    tiles.push({
      key: 'efficiency',
      value: `${insight.attempts_efficiency.toFixed(2)}×`,
      label: t('userProfile.insights.tiles.efficiency'),
    });
  }

  if (insight.rare_sends.length > 0) {
    tiles.push({
      key: 'rare',
      value: String(insight.rare_sends.length),
      label: t('userProfile.insights.tiles.rareSends'),
    });
  }

  return (
    <div
      className={`grid gap-3 ${tiles.length >= 3 ? 'grid-cols-3' : 'grid-cols-2'}`}
    >
      {tiles.map(({ key, value, label }) => (
        <Card key={key}>
          <CardContent className="flex flex-col items-center justify-center p-4 text-center">
            <span className="text-2xl font-bold tabular-nums">{value}</span>
            <span className="mt-1 text-xs text-muted-foreground">{label}</span>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}
